<script lang="ts">
  import { yyyy } from "../store.svelte";
  import { onMount, onDestroy } from "svelte";
  import { fade } from "svelte/transition";

  let { index = 0, g = "", wireident = "" } = $props();

  let status: any = $state(null);
  let intervalId: number | null = null;
  let wavePath: SVGPathElement;
  let animationId: number | null = null;
  let morphIntervalId: number | null = null;
  let fluffIntervalId: number | null = null;
  let currentFluff = $state("");
  let showInitialMessage = $state(true);
  let gridContainer: HTMLDivElement;
  let scrollDirection = 1;
  let scrollIntervalId: number | null = null;
  let scrollPaused = false;

  function autoScroll() {
    if (!gridContainer || scrollPaused) return;

    const maxScroll = gridContainer.scrollHeight - gridContainer.clientHeight;
    if (maxScroll <= 0) return;

    gridContainer.scrollTop += scrollDirection * 1;

    if (gridContainer.scrollTop >= maxScroll) {
      scrollDirection = -1;
      scrollPaused = true;
      setTimeout(() => { scrollPaused = false; }, 2000);
    } else if (gridContainer.scrollTop <= 0) {
      scrollDirection = 1;
      scrollPaused = true;
      setTimeout(() => { scrollPaused = false; }, 2000);
    }
  }

  const fluff = [
    "Coalescing electrons",
    "Unravelling voltages",
    "Forwarding faradays",
    "Sidestepping ohms",
    "Increasing capacitance",
    "Quenching electrical arcs",
    "Amalgamating phases",
    "Directing alternating current",
    "Alternating direct current",
    "Monitoring magnetic flux",
    "Seeking quarks",
    "Avoiding sparks",
    "Balancing load centers",
    "Routing power flows",
    "Synchronizing substations",
    "Mapping feeder topology",
    "Calibrating transformers",
    "Tracing conductor paths",
    "Optimizing switch positions",
			"Teaching current which way to flow",
"Reminding voltage to stay nominal",
    "Calculating impedance",
    "Resolving fault currents",
    "Energizing bus bars",
    "Sequencing reclosers",
    "Validating protection zones",
    "Meshing distribution grids",
    "Harmonizing frequencies",
    "Dispatching megawatts",
    "Stabilizing voltage profiles",
  ];

  function cycleFluff() {
    currentFluff = fluff[Math.floor(Math.random() * fluff.length)];
  }

  // Wave morphing state
  let currentWavelength = $state(60);
  let currentAmplitude = $state(6);
  let targetWavelength = 60;
  let targetAmplitude = 6;
  let startWavelength = 60;
  let startAmplitude = 6;
  let morphStartTime: number | null = null;
  const morphDuration = 3000; // ms to transition

  // Scroll state
  let scrollOffset = 0;
  let lastTimestamp = 0;
  let waveSvg: SVGSVGElement;
  const scrollSpeed = 60; // pixels per second

  function generateWavePath(width: number, height: number, wavelength: number, amplitude: number): string {
    const numPoints = 200;
    const points: {x: number, y: number}[] = [];
    const centerY = height / 2;

    for (let i = 0; i <= numPoints; i++) {
      const x = (i / numPoints) * width;
      const phase = (2 * Math.PI * x) / wavelength;
      const y = centerY - amplitude * Math.sin(phase);
      points.push({ x, y });
    }

    let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
    for (let i = 1; i < points.length; i++) {
      d += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
    }
    return d;
  }

  function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animateWave(timestamp: number) {
    const delta = timestamp - lastTimestamp;
    lastTimestamp = timestamp;

    // Update scroll - wrap based on current wavelength
    scrollOffset += (delta / 1000) * scrollSpeed;
    scrollOffset = scrollOffset % currentWavelength;

    if (waveSvg) {
      waveSvg.style.transform = `translateX(${-scrollOffset}px)`;
    }

    // Update morph
    if (morphStartTime === null) morphStartTime = timestamp;

    const elapsed = timestamp - morphStartTime;
    const progress = Math.min(1, elapsed / morphDuration);
    const easedProgress = easeInOutCubic(progress);

    currentWavelength = startWavelength + (targetWavelength - startWavelength) * easedProgress;
    currentAmplitude = startAmplitude + (targetAmplitude - startAmplitude) * easedProgress;

    if (wavePath) {
      wavePath.setAttribute('d', generateWavePath(960, 20, currentWavelength, currentAmplitude));
    }

    animationId = requestAnimationFrame(animateWave);
  }

  function switchWaveform() {
    startWavelength = currentWavelength;
    startAmplitude = currentAmplitude;
    morphStartTime = null;
    // Random wavelength between 40 and 120, amplitude between 4 and 8
    targetWavelength = 40 + Math.random() * 80;
    targetAmplitude = 4 + Math.random() * 4;
  }

  async function pollStatus() {
    if (!wireident) return;

    try {
      const response = await fetch(`${yyyy.uri.api}/rpc/generating_status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + yyyy.sesh.logtoken.token
        },
        body: JSON.stringify({ wireident })
      });

      if (response.ok) {
        status = await response.json();
      }
    } catch (err) {
      console.error("Error polling status:", err);
    }
  }

  onMount(() => {
    pollStatus();
    intervalId = setInterval(pollStatus, 2000);

    // Start wave animation
    animationId = requestAnimationFrame(animateWave);

    // Switch waveform every 7 seconds
    const randomSwitch = () => {
      switchWaveform();
      morphIntervalId = setTimeout(randomSwitch, 7000) as unknown as number;
    };
    morphIntervalId = setTimeout(randomSwitch, 7000) as unknown as number;

    // Start with initial message, then cycle fluff after 11 seconds
    setTimeout(() => {
      showInitialMessage = false;
      cycleFluff();
      fluffIntervalId = setInterval(cycleFluff, 8775);
    }, 11000);

    // Auto-scroll the grid
    scrollIntervalId = setInterval(autoScroll, 125);
  });

  onDestroy(() => {
    if (intervalId) clearInterval(intervalId);
    if (animationId) cancelAnimationFrame(animationId);
    if (morphIntervalId) clearTimeout(morphIntervalId);
    if (fluffIntervalId) clearInterval(fluffIntervalId);
    if (scrollIntervalId) clearInterval(scrollIntervalId);
  });
</script>

<div id="lvwr-{String(index + 1).padStart(2, '0')}" class="column is-one-quarter porridge">
  <div class="card lvwr-card">
    <div class="card-image map-holder">
      <div class="overlay">
        <div class="status-display">
          {#if status?.generating || !status}
            <div class="fluff-container">
              {#if showInitialMessage}
                <span class="fluff-text" out:fade={{ duration: 500 }}>Setting up your livewire<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span></span>
              {:else}
                {#key currentFluff}
                  <span class="fluff-text" in:fade={{ duration: 500 }} out:fade={{ duration: 500 }}>{currentFluff}</span>
                {/key}
              {/if}
            </div>
          {:else if status && Array.isArray(status)}
            {@const energizingFeeders = status.find(s => s.status === "Energizing")?.feeders || []}
            {@const energizedFeeders = status.find(s => s.status === "Energized")?.feeders || []}
            {@const dormantFeeders = status.find(s => s.status === "DORMANT")?.feeders || []}
            {@const allFeeders = [...energizingFeeders, ...energizedFeeders, ...dormantFeeders].sort()}
            <div class="feeder-grid" bind:this={gridContainer}>
              {#each allFeeders as feeder}
                {@const isEnergizing = energizingFeeders.includes(feeder)}
                {@const isEnergized = energizedFeeders.includes(feeder)}
                <div class="grid-cell" class:energizing={isEnergizing} class:energized={isEnergized} class:dormant={!isEnergizing && !isEnergized}>
                  {#if isEnergizing}
                    <span class="cell-name">{feeder}</span>
                    <span class="pulse-dot"></span>
                  {:else if isEnergized}
                    <span class="cell-check">✓</span>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        </div>
        {#if status?.generating || !status}
          <div class="wave-container">
            <svg bind:this={waveSvg} class="sine-wave" width="960" height="20" viewBox="0 0 960 20">
              <path
                bind:this={wavePath}
                fill="none"
                stroke="#ff3e00"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  .porridge {
    width: 475px;
  }

  .lvwr-card {
    height: 475px;
  }

  .map-holder {
    height: 100%;
    position: relative;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    z-index: 10;
  }

  .wave-container {
    width: 100%;
    height: 20px;
    overflow: hidden;
  }

  .sine-wave {
    /* scroll handled in JS for seamless wavelength changes */
  }

  .status-display {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .status-row {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .status-label {
    color: #ff3e00;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .status-count {
    color: #ff3e00;
    font-size: 1.2rem;
    font-weight: 700;
  }

  .overlay-text {
    color: #ff3e00;
    font-family: 'Ubuntu', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .feeder-grid {
    width: 95%;
    max-height: 400px;
    overflow-y: auto;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(24px, 1fr));
    gap: 4px;
    align-content: start;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .feeder-grid::-webkit-scrollbar {
    display: none;
  }

  .grid-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    transition: all 0.3s ease;
  }

  .grid-cell.dormant {
    aspect-ratio: 1;
    background: #ccc;
  }

  .grid-cell.energized {
    aspect-ratio: 1;
    background: #00d1b2;
  }

  .grid-cell.energizing {
    grid-column: span 5;
    padding: 4px 8px;
    background: rgba(255, 62, 0, 0.15);
    border: 1px solid #ff3e00;
    gap: 6px;
    max-width: 100%;
    overflow: hidden;
  }

  .cell-name {
    font-size: 0.65rem;
    color: #ff3e00;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .cell-check {
    color: white;
    font-size: 0.6rem;
    font-weight: bold;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ff3e00;
    animation: pulse-glow 1s ease-in-out infinite;
    flex-shrink: 0;
  }

  @keyframes pulse-glow {
    0%, 100% { opacity: 0.4; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }

  .bar-graph {
    width: 90%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .bar-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .bar-label {
    color: #333;
    font-size: 0.8rem;
    width: 70px;
    text-align: right;
  }

  .bar-container {
    flex: 1;
    height: 24px;
    background: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    min-width: 150px;
  }

  .bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.5s ease;
    min-width: 4px;
  }

  .energized-bar {
    background: #00d1b2;
  }

  .dormant-bar {
    background: #999;
  }

  .bar-count {
    color: #333;
    font-size: 0.8rem;
    width: 30px;
  }


  .fluff-container {
    position: relative;
    width: 90%;
    height: 3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .fluff-text {
    position: absolute;
    width: 100%;
    color: #ff3e00;
    font-size: 1.5rem;
    font-weight: 500;
    font-style: italic;
    text-align: center;
  }

  .dot {
    animation: pulse-dot 1.5s ease-in-out infinite;
  }

  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }

</style>
